import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Menu, X, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import TeamManagementModal from "@/components/TeamManagementModal";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Close user dropdown when clicking outside
  React.useEffect(() => {
    if (!userMenuOpen) return;
    const onClick = (e) => {
      if (!e.target.closest("[data-user-menu-root]")) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [userMenuOpen]);

  // Auto-close mobile menu when route changes
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open
  React.useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const linkClass = ({ isActive }) =>
    `relative text-sm font-medium py-2 transition-colors ${
      isActive ? "text-[var(--text)]" : "text-[var(--muted)] hover:text-[var(--text)]"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block text-sm font-medium py-3 border-b ${
      isActive ? "text-[var(--text)] font-semibold" : "text-[var(--muted)]"
    }`;

  const isSuper = user?.role === "superadmin";
  const navItems = isSuper
    ? [
        { to: "/utilisateurs", label: "Utilisateurs", testid: "nav-users", end: true },
        { to: "/aide", label: "Aide", testid: "nav-help" },
      ]
    : [
        { to: "/", label: "Accueil", testid: "nav-home", end: true },
        { to: "/evenements", label: "Événements", testid: "nav-events" },
        { to: "/officiels", label: "Officiels", testid: "nav-officials" },
        { to: "/paies", label: "Paies", testid: "nav-payroll" },
        { to: "/reglages", label: "Réglages", testid: "nav-settings" },
        { to: "/aide", label: "Aide", testid: "nav-help" },
      ];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b" style={{ background: "rgba(245, 245, 240, 0.85)", borderColor: "var(--border)" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <Logo size={36} />
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="main-nav">
            {navItems.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} className={linkClass} data-testid={it.testid}>
                {({ isActive }) => (
                  <>
                    {it.label}
                    {isActive && <span className="absolute left-0 right-0 -bottom-px h-[2px] rounded-full" style={{ background: "var(--accent)" }} />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* User pill + dropdown (admins only — superadmin has its own /utilisateurs page). */}
            {user?.role !== "superadmin" ? (
              <div className="relative" data-user-menu-root>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="hidden sm:flex flex-col items-end text-right hover:opacity-80 cursor-pointer"
                  data-testid="user-menu-toggle"
                  aria-expanded={userMenuOpen}
                >
                  <div className="label !text-[10px] !mb-0">Gestionnaire</div>
                  <div className="text-xs text-[var(--text)] truncate max-w-[180px]">{user?.email}</div>
                </button>
                {userMenuOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 min-w-[220px] rounded-lg shadow-lg border overflow-hidden z-50"
                    style={{ background: "var(--bg)", borderColor: "var(--border-strong)" }}
                    data-testid="user-menu-dropdown"
                  >
                    <button
                      type="button"
                      onClick={() => { setUserMenuOpen(false); setTeamOpen(true); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-black/5 border-b text-left"
                      style={{ borderColor: "var(--border)" }}
                      data-testid="user-menu-team"
                    >
                      <Users size={15} strokeWidth={2.2} />
                      Gérer l&apos;équipe
                    </button>
                    <button
                      type="button"
                      onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-black/5 text-left"
                      data-testid="user-menu-logout"
                    >
                      <LogOut size={15} strokeWidth={2.2} />
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:block text-right">
                <div className="label !text-[10px] !mb-0">Super Admin</div>
                <div className="text-xs text-[var(--text)] truncate max-w-[180px]">{user?.email}</div>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="btn-secondary !px-2.5 !py-2"
              title="Déconnexion"
              data-testid="logout-btn"
            >
              <LogOut size={16} strokeWidth={2.2} />
            </button>
            {/* Hamburger (mobile only) */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden btn-secondary !px-2.5 !py-2"
              title="Menu"
              aria-label="Menu"
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={16} strokeWidth={2.2} /> : <Menu size={16} strokeWidth={2.2} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer + overlay (outside <header> to escape its stacking context) */}
      <div
        className={`md:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      <div
        className={`md:hidden fixed top-0 right-0 bottom-0 z-[70] w-[78%] max-w-xs border-l shadow-2xl transform transition-transform duration-300 ease-out ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ background: "var(--bg)", borderColor: "var(--border-strong)" }}
        data-testid="mobile-nav"
        aria-hidden={!mobileOpen}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <Logo size={32} />
          <button
            onClick={() => setMobileOpen(false)}
            className="btn-secondary !px-2.5 !py-2"
            aria-label="Fermer le menu"
            data-testid="mobile-menu-close"
          >
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>
        <nav className="h-[calc(100%-65px)] overflow-y-auto px-5 py-4">
          {navItems.map((it, idx) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={mobileLinkClass}
              style={{
                borderColor: "var(--border)",
                transitionDelay: mobileOpen ? `${idx * 40}ms` : "0ms",
                opacity: mobileOpen ? 1 : 0,
                transform: mobileOpen ? "translateX(0)" : "translateX(20px)",
                transition: "opacity 250ms ease-out, transform 250ms ease-out",
              }}
              data-testid={`mobile-${it.testid}`}
            >
              {it.label}
            </NavLink>
          ))}
          {user?.email && (
            <div
              className="pt-6 label leading-relaxed"
              style={{
                opacity: mobileOpen ? 1 : 0,
                transition: `opacity 300ms ease-out ${navItems.length * 40 + 80}ms`,
              }}
            >
              <div>{user.role === "superadmin" ? "Super Admin" : "Gestionnaire"}</div>
              <div className="text-[var(--text)] break-all mt-0.5 normal-case tracking-normal">{user.email}</div>
            </div>
          )}
        </nav>
      </div>

      <main className="w-full max-w-screen-2xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Outlet />
      </main>

      {teamOpen && (
        <TeamManagementModal
          currentUserId={user?.id}
          onClose={() => setTeamOpen(false)}
        />
      )}

      <footer className="border-t mt-24" style={{ borderColor: "var(--border)" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-4 md:px-8 py-6 flex items-center justify-between">
          <span className="label">Gestion Dek · v2.0</span>
          <span className="label">{new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
